import { useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { Form, Modal, Result } from 'antd';

import { BundleWithChildren } from '@app/domain-bundle-interfaces';

import { useEditBundle } from '../data';

import { useEditBundleAccess } from './rules';
import { BundleFormData, useBundleForm } from './use-bundle-form';

export type EditFormProps = {
  record: BundleWithChildren;
};

export const EditForm = ({ record }: EditFormProps) => {
  const { hasBundleOwnership } = useEditBundleAccess();
  const editBundle = useEditBundle(record.id);

  const initialValues: BundleFormData = useMemo(
    () => ({
      ...record,
      productFamily: record.productFamily
        ? { value: record.productFamily.externalId, label: record.productFamily.name }
        : undefined,
      accesses: record.accesses.map((x) => x.access),
      owners: record.owners.map((x) => ({
        value: x.owner.enterpriseId,
        label: x.owner.name,
      })),
      accessDependencies:
        record.accessDependencies?.map((d) => ({ child: d.child.access, parent: d.parent.access })) ?? [],
      anchors: record.accesses.filter((x) => x.isEagerAnchor).map((x) => x.access),
    }),
    [record],
  );

  const { getFormProps, getManageDependenciesModalProps, getSuccessResultProps } = useBundleForm(
    editBundle,
    initialValues,
    record.id,
  );

  if (editBundle.isSuccess && editBundle.data.data.isSuccess) {
    return (
      <Result
        {...getSuccessResultProps()}
        subTitle={`Bundle name: ${editBundle.data.data.mutated?.name}`}
        title="Successfully edited a bundle!"
      />
    );
  }

  if (!hasBundleOwnership(record)) {
    return <Navigate to="/access-forbidden" />;
  }

  return (
    <>
      <Modal {...getManageDependenciesModalProps()} />
      <Form {...getFormProps()} />
    </>
  );
};
